import { AnimatePresence } from 'framer-motion';
import filter from 'lodash/filter';
import orderBy from 'lodash/orderBy';
import uniqueId from 'lodash/uniqueId';
import React, { useState } from 'react';

import RichText from 'components/RichText/RichText';
import { AwardsSectionProps } from 'containers/AboutPage/sections/AwardsSection';
import { useCopyStore } from 'store';

import { ColorNames } from '../../utils/styles/theme';
import Badge from '../Badge';
import { awardsCustomSort, orderYears } from './AwardsTable.utils';

import * as S from './AwardsTable.styles';

export interface AwardsTableProps extends AwardsSectionProps {}

const AwardsTable: React.FunctionComponent<AwardsTableProps> = ({
  organisationLabel,
  projectLabel,
  awardLabel,
  sectionTitle,
  awards,
}) => {
  const { globalSettings } = useCopyStore(({ copy }) => copy);
  const years = orderYears(awards.map(p => p.year.trim()));

  const [selectedYear, setSelectedYear] = useState(years[0]);

  const filteredAwards = awardsCustomSort(
    orderBy(
      filter(awards, e => e.year.trim() === selectedYear.trim()),
      'organisation',
      ['asc']
    )
  );

  const filters = (year: string) => (
    <Badge
      as="button"
      backgroundColor={
        year === selectedYear
          ? ColorNames.lighterMineShaft
          : ColorNames.concrete
      }
      hoverBackground={
        year === selectedYear ? ColorNames.lighterMineShaft : ColorNames.mercury
      }
      hoverTextColor={
        globalSettings?.buttonsHoverColor ||
        globalSettings?.buttonsHoverDefaultColors
      }
      onClick={() => setSelectedYear(year)}
      key={uniqueId('year_filter_')}
    >
      <S.FilterInner active={year === selectedYear}>{year}</S.FilterInner>
    </Badge>
  );

  const tableHead = (
    <S.TableRow>
      <S.TableHeadText>{organisationLabel}</S.TableHeadText>
      <S.TableHeadText>{projectLabel}</S.TableHeadText>
      <S.TableHeadText>{awardLabel}</S.TableHeadText>
    </S.TableRow>
  );

  const awardsList = [...filteredAwards[0], ...filteredAwards[1]].map(
    ({ organisation, project, name }) => (
      <S.TableRow
        initial={{ opacity: 0 }}
        exit={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        key={uniqueId('award_table_')}
      >
        <S.TableText>{organisation}</S.TableText>
        <S.TableText>{project}</S.TableText>
        <S.TableText>{name}</S.TableText>
      </S.TableRow>
    )
  );

  return (
    <S.AwardsTableWrapper>
      <S.Heading>
        <RichText
          as="h1"
          size="heading1"
          richText={sectionTitle}
          defaultFormatting
        />
      </S.Heading>

      <S.Filters>{years.map(year => filters(year))}</S.Filters>

      <S.TableWrapper>
        {tableHead}

        <AnimatePresence mode="popLayout">{awardsList}</AnimatePresence>
      </S.TableWrapper>
    </S.AwardsTableWrapper>
  );
};

export default AwardsTable;
